import React, { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import Link from '@dashboard/components/Link';
import { Form } from '@dashboard/components/Formik';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import { toast } from '@dashboard/context/ToastContext';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import SettingsContainer from '@/dashboard/components/SettingsContainer';
import NavTabs from '@dashboard/components/NavTabs';
import PageHeader from '@dashboard/components/PageHeader';
import Dialog from '@dashboard/components/Dialog';
import { DataGrid } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Badge } from '@dashboard/components/ui/badge';
import { Button } from '@dashboard/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import RadioCard from '@dashboard/components/Form/components/RadioCard';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { fetcher } from '@dashboard/services/request';
import { sellerTaxRatesKey } from '@dashboard/lib/queryKeys';
import CreateTaxRateForm from '@dashboard/scenes/PageEdit/scenes/PageCheckout/components/CreateTaxRateForm';

export const updateTaxRateValidationSchema = Yup.object().shape({
  displayName: Yup.string()
    .required('Tax name is required')
    .max(50, 'Tax name must be 50 characters or less'),
});

const UpdateTaxRateForm = ({ taxRate, onSuccess }) => {
  const { seller, token } = useAuth();
  const queryClient = useQueryClient();

  const updateTaxRate = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${seller._id}/tax-rates/${taxRate.id}`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sellerTaxRatesKey(seller._id) });
      toast({ title: 'Tax rate updated' });
      if (onSuccess) onSuccess();
    },
  });

  return (
    <Form
      enableReinitialize
      showState={false}
      formSubmitAlign="end"
      submitButton={{ label: 'Save changes' }}
      initialValues={{ displayName: taxRate.displayName, default: taxRate.default ?? false }}
      validationSchema={updateTaxRateValidationSchema}
      fields={[
        {
          name: 'displayName',
          type: 'text',
          label: 'Tax name',
          required: true,
          placeholder: 'VAT, Sales Tax, GST',
        },
      ]}
      onSubmit={async values => {
        try {
          await updateTaxRate.mutateAsync(values);
          return { data: true };
        } catch (error) {
          return { error };
        }
      }}
    />
  );
};

const EditTaxRateDialog = ({ open, taxRate, onClose }) => (
  <Dialog
    title="Edit tax rate"
    size="md"
    open={open}
    onCancel={onClose}
    cancelButton={null}
    confirmButton={null}
  >
    {taxRate && <UpdateTaxRateForm taxRate={taxRate} onSuccess={onClose} />}
  </Dialog>
);

const DefaultTaxBehaviorTab = () => {
  const { seller, token } = useAuth();
  const { updateSeller } = useSellerQueries();

  const { data: taxRates = [], isLoading: isLoadingTaxRates } = useQuery({
    queryKey: sellerTaxRatesKey(seller?._id),
    queryFn: () => fetcher(`/sellers/${seller._id}/tax-rates`, token),
    enabled: !!seller?._id && !!token,
  });

  const setDefault = useMutation({
    mutationFn: mode => updateSeller({ tax: { ...(seller?.tax ?? {}), mode } }),
    onSuccess: () => toast({ title: 'Default tax behavior updated' }),
    onError: () => toast({ title: 'Failed to update default tax behavior', type: 'error' }),
  });

  const [optimisticMode, setOptimisticMode] = React.useState(null);
  // Fallback for sellers whose `mode` field hasn't been written yet (pre-migration).
  // Mirrors the migration: legacy `tax.enabled === true` means Stripe Tax, otherwise no tax.
  const currentMode = seller?.tax?.mode ?? (seller?.tax?.enabled === true ? 'stripe' : 'none');
  const displayedMode = optimisticMode ?? currentMode;

  React.useEffect(() => {
    if (optimisticMode && optimisticMode === currentMode) {
      setOptimisticMode(null);
    }
  }, [currentMode, optimisticMode]);

  if (!seller?._id) return null;

  const defaultRate = taxRates.find(rate => rate.default);
  const stripeTaxEnabled = seller.tax?.enabled === true;

  const fixedStatus = isLoadingTaxRates
    ? 'Loading…'
    : taxRates.length === 0
      ? 'No tax rates configured'
      : defaultRate
        ? `${taxRates.length} tax rate${taxRates.length === 1 ? '' : 's'} · default: ${defaultRate.displayName}`
        : `${taxRates.length} tax rate${taxRates.length === 1 ? '' : 's'}`;

  const handleModeChange = mode => {
    setOptimisticMode(mode);
    setDefault.mutate(mode, {
      onError: () => setOptimisticMode(null),
    });
  };

  return (
    <SettingsContainer size="2xl">
      <SettingsHeader>Tax behavior</SettingsHeader>

      <p className="mb-4 text-sm text-gray-500 dark:text-muted-foreground">
        Choose how tax is applied by default to new checkouts and events. You can override this per
        checkout page or event.
      </p>

      <RadioCard.Group name="tax-mode" isInline={false}>
        <RadioCard.Card
          name="tax-mode"
          value="none"
          checked={displayedMode === 'none'}
          onChange={() => handleModeChange('none')}
        >
          <RadioCard.Label>
            <span className="flex items-center gap-2">
              No tax
              {displayedMode === 'none' && <Badge variant="success">Default</Badge>}
            </span>
          </RadioCard.Label>
          <RadioCard.LabelDescription>
            No tax is charged on new checkouts and events.
          </RadioCard.LabelDescription>
        </RadioCard.Card>

        <RadioCard.Card
          name="tax-mode"
          value="fixed"
          checked={displayedMode === 'fixed'}
          onChange={() => handleModeChange('fixed')}
          footer={
            <>
              <Badge variant="outline">{fixedStatus}</Badge>
              <Button
                to="/settings/tax/fixed-tax-rates"
                variant="outline"
                size="sm"
                onClick={e => e.stopPropagation()}
              >
                Manage tax rates
              </Button>
            </>
          }
        >
          <RadioCard.Label>
            <span className="flex items-center gap-2">
              Fixed tax rate
              {displayedMode === 'fixed' && <Badge variant="success">Default</Badge>}
            </span>
          </RadioCard.Label>
          <RadioCard.LabelDescription>
            Charge the same tax rate to every customer.
          </RadioCard.LabelDescription>
        </RadioCard.Card>

        <RadioCard.Card
          name="tax-mode"
          value="stripe"
          checked={displayedMode === 'stripe'}
          onChange={() => handleModeChange('stripe')}
          footer={
            <>
              <Badge variant="outline">
                {stripeTaxEnabled ? 'Enabled in Stripe' : 'Not enabled'}
              </Badge>
              <Button
                to="/settings/tax/stripe-tax"
                variant="outline"
                size="sm"
                onClick={e => e.stopPropagation()}
              >
                Manage Stripe Tax
              </Button>
            </>
          }
        >
          <RadioCard.Label>
            <span className="flex items-center gap-2">
              Stripe Tax
              {displayedMode === 'stripe' && <Badge variant="success">Default</Badge>}
            </span>
          </RadioCard.Label>
          <RadioCard.LabelDescription>
            Automatically calculate tax based on your customer&apos;s location.
          </RadioCard.LabelDescription>
        </RadioCard.Card>
      </RadioCard.Group>
    </SettingsContainer>
  );
};

const DeleteTaxRateDialog = ({ open, taxRate, onClose }) => {
  const { seller, token } = useAuth();
  const queryClient = useQueryClient();

  const deleteTaxRate = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${seller._id}/tax-rates/${taxRate?.id}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sellerTaxRatesKey(seller._id) });
      toast({ title: 'Tax rate deleted', type: 'success' });
      onClose();
    },
    onError: () => {
      toast({ title: 'Failed to delete tax rate', type: 'error' });
    },
  });

  return (
    <Dialog
      title="Delete this tax rate?"
      size="md"
      open={open}
      onCancel={onClose}
      cancelButton={{ label: 'Cancel', appearance: 'secondary', onClick: onClose }}
      confirmButton={{
        label: deleteTaxRate.isPending ? 'Deleting...' : 'Delete',
        appearance: 'primary',
        disabled: deleteTaxRate.isPending,
        onClick: () => deleteTaxRate.mutate(),
      }}
    >
      <p className="text-sm text-gray-600 dark:text-muted-foreground">
        {taxRate?.displayName ? (
          <>
            <span className="font-bold">
              {taxRate.displayName} ({taxRate.percentage}%)
            </span>{' '}
            will be removed from any checkouts and events using it.
          </>
        ) : (
          'This rate will be removed from any pages that use it.'
        )}
      </p>
    </Dialog>
  );
};

const FixedTaxRatesTab = ({ isCreateDialogOpen, setIsCreateDialogOpen }) => {
  const { seller, token } = useAuth();
  const queryClient = useQueryClient();
  const [editingTaxRate, setEditingTaxRate] = useState(null);
  const [deletingTaxRate, setDeletingTaxRate] = useState(null);

  const { data: taxRates = [], isLoading } = useQuery({
    queryKey: sellerTaxRatesKey(seller?._id),
    queryFn: () => fetcher(`/sellers/${seller._id}/tax-rates`, token),
    enabled: !!seller?._id && !!token,
  });

  const createTaxRate = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${seller._id}/tax-rates`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sellerTaxRatesKey(seller._id) }),
  });

  const setDefault = useMutation({
    mutationFn: ({ taxRateId, isDefault }) =>
      fetcher(`/sellers/${seller._id}/tax-rates/${taxRateId}`, token, {
        method: 'POST',
        body: JSON.stringify({ default: isDefault }),
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: sellerTaxRatesKey(seller._id) });
      toast({
        title: variables.isDefault ? 'Default tax rate updated' : 'Default tax rate removed',
      });
    },
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: 'displayName',
        header: 'Name',
        cell: ({ row }) => row.original.displayName,
      },
      {
        accessorKey: 'percentage',
        header: 'Rate',
        cell: ({ row }) => `${row.original.percentage}%`,
      },
      {
        accessorKey: 'inclusive',
        header: 'Behavior',
        cell: ({ row }) => (row.original.inclusive ? 'Inclusive' : 'Exclusive'),
      },
      {
        accessorKey: 'default',
        header: 'Default rate',
        cell: ({ row }) => (row.original.default ? 'Yes' : '—'),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" aria-label="Tax rate actions">
                <EllipsisVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setEditingTaxRate(row.original)}>
                Edit
              </DropdownMenuItem>
              {row.original.default ? (
                <DropdownMenuItem
                  onSelect={() =>
                    setDefault.mutate({ taxRateId: row.original.id, isDefault: false })
                  }
                >
                  Remove default
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onSelect={() =>
                    setDefault.mutate({ taxRateId: row.original.id, isDefault: true })
                  }
                >
                  Make default
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={() => setDeletingTaxRate(row.original)}
                variant="destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [setDefault],
  );

  const emptyState = (
    <EmptyState>
      <EmptyState.Title>No tax rates yet</EmptyState.Title>
      <EmptyState.Subtitle>Fixed tax rates will be shown here.</EmptyState.Subtitle>
    </EmptyState>
  );

  return (
    <SettingsContainer size="2xl">
      <SettingsHeader>Fixed tax rates</SettingsHeader>

      <div className="space-y-4">
        <p className="text-sm text-gray-500 dark:text-muted-foreground">
          Fixed tax rates apply the same rate to every customer, regardless of location. Create
          rates here, then assign them to your checkout pages and events.
        </p>

        <DataGrid columns={columns} data={taxRates} isLoading={isLoading} emptyState={emptyState} />

        <Dialog
          title="Create tax rate"
          description="Fixed tax rates apply the same rate to every customer, regardless of location."
          size="md"
          open={isCreateDialogOpen}
          onCancel={() => setIsCreateDialogOpen(false)}
          cancelButton={null}
          confirmButton={null}
        >
          {({ close }) => (
            <CreateTaxRateForm
              isPending={createTaxRate.isPending}
              onSubmit={async (values, { resetForm }) => {
                await createTaxRate.mutateAsync(values);
                resetForm();
                close();
              }}
            />
          )}
        </Dialog>

        <EditTaxRateDialog
          open={!!editingTaxRate}
          taxRate={editingTaxRate}
          onClose={() => setEditingTaxRate(null)}
        />

        <DeleteTaxRateDialog
          open={!!deletingTaxRate}
          taxRate={deletingTaxRate}
          onClose={() => setDeletingTaxRate(null)}
        />
      </div>
    </SettingsContainer>
  );
};

const StripeTaxTab = () => {
  const { seller } = useAuth();
  const { updateSeller } = useSellerQueries();

  return (
    <SettingsContainer size="2xl">
      <SettingsHeader id="enable">Stripe Tax</SettingsHeader>

      <p className="mb-4 text-sm text-gray-500 dark:text-muted-foreground">
        Before enabling, make sure Stripe Tax is enabled in Stripe, your tax registrations are set
        up, and your checkout collects the required address fields.
      </p>

      {seller?._id && (
        <Form
          autosave
          showState={false}
          initialValues={{
            tax: seller.tax,
          }}
          fields={[
            {
              name: 'tax.enabled',
              required: false,
              type: 'checkbox',
              label: 'Enable Stripe Tax',
            },
          ]}
          onSubmit={async values => {
            try {
              const { data } = await updateSeller(values);

              return {
                data,
                onSuccess: () => {
                  toast({
                    title: data?.tax?.enabled ? 'Stripe Tax enabled' : 'Stripe Tax disabled',
                  });
                },
              };
            } catch (error) {
              return {
                error,
              };
            }
          }}
        />
      )}

      <SettingsDivider />

      <SettingsHeader id="set-up">How to set up Stripe Tax</SettingsHeader>

      <ol className="list-decimal list-inside space-y-2 mb-4 text-sm">
        <li>
          Enable Stripe Tax in your{' '}
          <Link
            href={getStripeDashboardUrl('/tax', { seller })}
            target="_blank"
            external
            variant="secondary"
          >
            Stripe Dashboard
          </Link>
        </li>
        <li>
          Set your{' '}
          <Link
            href={getStripeDashboardUrl('/settings/stripe-tax', { seller })}
            target="_blank"
            external
            variant="secondary"
          >
            default tax behavior
          </Link>{' '}
          (inclusive or exclusive)
        </li>
        <li>
          Add your{' '}
          <Link
            href={getStripeDashboardUrl('/tax/registrations', { seller })}
            target="_blank"
            external
            variant="secondary"
          >
            tax registrations
          </Link>
        </li>
        <li>Make sure your checkout collects the required address fields (see below)</li>
      </ol>

      <SettingsDivider />

      <SettingsHeader id="required-fields">Required address fields</SettingsHeader>

      <p className="mb-4 text-sm">To calculate tax, your checkout must collect:</p>

      <ul className="list-disc list-inside space-y-1 mb-4 text-sm">
        <li>
          <strong>US:</strong> Country, address line 1, postal code
        </li>
        <li>
          <strong>Canada & EU:</strong> Country, postal code
        </li>
        <li>
          <strong>UK & other countries:</strong> Country
        </li>
      </ul>

      <p className="text-sm">
        If you sell B2B, add a Tax ID field (e.g. VAT number) to your checkout.
      </p>
    </SettingsContainer>
  );
};

const SettingsTax = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const isFixedTaxRates = useRouteMatch('/settings/tax/fixed-tax-rates');
  const location = useLocation();

  const tabs = [
    {
      name: 'Tax behavior',
      href: '/settings/tax',
      render: () => <DefaultTaxBehaviorTab />,
    },
    {
      name: 'Fixed tax rates',
      href: '/settings/tax/fixed-tax-rates',
      render: () => (
        <FixedTaxRatesTab
          isCreateDialogOpen={isCreateDialogOpen}
          setIsCreateDialogOpen={setIsCreateDialogOpen}
        />
      ),
    },
    {
      name: 'Stripe Tax',
      href: '/settings/tax/stripe-tax',
      render: () => <StripeTaxTab />,
    },
  ];

  return (
    <div className="p-4 space-y-4">
      <PageHeader
        title="Tax"
        description={
          <>
            Configure how tax is calculated and applied to your checkouts and events.{' '}
            <Link href="/docs/articles/tax" color="default" blank>
              Learn more
            </Link>
          </>
        }
        actions={
          isFixedTaxRates && (
            <Button appearance="primary" onClick={() => setIsCreateDialogOpen(true)}>
              Create tax rate
            </Button>
          )
        }
      >
        <NavTabs aria-label="Tax settings">
          {tabs.map(tab => (
            <NavTabs.Item key={tab.name} to={tab.href} active={location.pathname === tab.href}>
              {tab.name}
            </NavTabs.Item>
          ))}
        </NavTabs>
      </PageHeader>

      <Switch>
        <Route path="/settings/tax/stripe-tax" render={tabs[2].render} exact />
        <Route path="/settings/tax/fixed-tax-rates" render={tabs[1].render} exact />
        <Route path="/settings/tax" render={tabs[0].render} exact />
        <Route>
          <Redirect to="/settings/tax" />
        </Route>
      </Switch>
    </div>
  );
};

export default SettingsTax;
